// Frontend API integration for React

export interface BookingRequest {
  duration_days: number;
  transport_type: 'car' | 'car_bike';
  package_category: 'standard' | 'deluxe' | 'premium';
  group_size: number;
  selected_activities: string[];
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  state: string;
}

export interface EnquiryData {
  name: string;
  email: string;
  phone: string;
  country_code: string;
  date_of_travel?: string;
  traveller_count?: number;
  message?: string;
  source_page: string;
  itinerary_id?: string;
  itinerary_title?: string;
  session_id?: string;
}

export interface PricingResponse {
  duration_days: number;
  transport_type: string;
  package_category: string;
  group_size: number;
  base_price_per_person: number;
  total_price: number;
  price_breakdown: {
    base_package: number;
    transport_daily: number;
    fuel_charges: number;
    bike_rental: number;
    activities: number;
    taxes_fees: number;
    total_per_person: number;
  };
  itinerary: Array<{
    day: number;
    title: string;
    activities: string[];
  }>;
  terms_conditions: string[];
}

export interface ActivityCharge {
  activity_id: string;
  activity_name: string;
  price_per_person: number;
  description?: string;
  category: string;
}

export interface BookingConfirmation {
  booking_id: string;
  customer_details: {
    name?: string;
    email?: string;
    phone?: string;
  };
  package_details: PricingResponse;
  booking_status: string;
  payment_amount: number;
  payment_status: string;
  created_at: string;
}

interface ApiSuccess<T> {
  status: string;
  data: T;
  message?: string;
  timestamp?: string;
}

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const BOOKING_API_BASE_URL = `${API_BASE_URL}/api/v1/booking`;

async function parseApiResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = payload?.detail || payload?.message || `HTTP error! status: ${response.status}`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }

  return (payload && 'data' in payload ? (payload as ApiSuccess<T>).data : payload) as T;
}

export class TourismAPI {
  static async calculatePricing(request: BookingRequest): Promise<PricingResponse> {
    const response = await fetch(`${BOOKING_API_BASE_URL}/calculate-pricing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    return parseApiResponse<PricingResponse>(response);
  }

  static async createBooking(request: BookingRequest): Promise<BookingConfirmation> {
    const response = await fetch(`${BOOKING_API_BASE_URL}/create-booking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    return parseApiResponse<BookingConfirmation>(response);
  }

  static async getAvailablePackages() {
    const response = await fetch(`${BOOKING_API_BASE_URL}/packages`);
    return parseApiResponse<Record<string, unknown>>(response);
  }

  static async getAvailableActivities(state: string = 'ladakh'): Promise<{ activities: ActivityCharge[] }> {
    const normalizedState = state.toLowerCase().replace(/\s+pradesh$/i, '');
    const response = await fetch(`${BOOKING_API_BASE_URL}/activities?state=${normalizedState}`);
    return parseApiResponse<{ activities: ActivityCharge[] }>(response);
  }

  static async getDestinationCategories(state: string = 'ladakh') {
    const normalizedState = state.toLowerCase().replace(/\s+pradesh$/i, '');
    const response = await fetch(`${API_BASE_URL}/api/v1/itineraries/destinations/categories?state=${normalizedState}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  static async submitEnquiry(data: EnquiryData): Promise<{ status: string; data: { enquiry_id: string }; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/enquiry/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }
}

// Example usage in React component:
/*
import { TourismAPI, BookingRequest } from './api/tourism';

const handleCalculatePricing = async () => {
  const request: BookingRequest = {
    duration_days: selectedDays,
    transport_type: transportType,
    package_category: category,
    group_size: 4,
    selected_activities: selectedActivities
  };

  try {
    const pricing = await TourismAPI.calculatePricing(request);
    console.log('Pricing:', pricing);
  } catch (error) {
    console.error('Error calculating pricing:', error);
  }
};
*/